import { useState } from 'react';
import { Button, Dialog, Icon, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Two-factor, as one modal with three stops — item 11.                      │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The 9 Sep 2026 round asked for *"a modal with a QR code, the secret beneath it, the six-digit
 * field, then recovery keys that can be copied or downloaded, and removal behind a password"*. What
 * was there was a card that grew a secret and an input in place, with no QR code at all — so the
 * only way to enrol was to type a 32-character base32 string into a phone by hand.
 *
 * ⚠️⚠️ **THE QR CODE IS GOTRUE'S OWN SVG, NOT A LIBRARY.** `POST /auth/totp` already returns
 * `qr_code` — a complete `data:image/svg+xml` URL — and it has since C6; the card simply never
 * rendered it. Adding a QR generator to the bundle to redraw a string the server already drew
 * would be a dependency bought for nothing.
 *
 * ⚠️⚠️ **THE RECOVERY KEYS STOP IS NOT SKIPPABLE, AND THAT IS THE WHOLE REASON IT IS A STAGE
 * RATHER THAN A LINE ON THE CARD.** `0102` stores hashes; this response is the only time the keys
 * exist in readable form anywhere, and nothing — not us, not GoTrue, not an operator — can produce
 * them again. So the dialog does not close on the verify step, and the button that leaves this
 * stage says *"I have saved them"* rather than *"close"*.
 *
 * ⚠️ **Presentational.** Every request is the route's; this file holds the stage, the two fields
 * and the copy/download affordances. `person-screen.tsx` records what putting `useSession` in a
 * screen does to its own tests.
 */

export type TwoFactorStage =
  /** Scan the code, then type six digits. */
  | 'enrol'
  /** The keys, once. */
  | 'keys'
  /** Turning it off, behind the password. */
  | 'disable'
  /** A fresh set of keys, behind the password. */
  | 'regenerate';

export interface TwoFactorEnrolment {
  readonly secret: string;
  /**
   * ⚠️⚠️ **RAW SVG MARKUP, NOT A URL, AND ASSUMING OTHERWISE IS WHY THE CODE NEVER DREW.** Measured
   * against the running stack: `POST /auth/totp` answers `qr_code` with an `<?xml …?><svg …>`
   * document — GoTrue generates it with SVGo. An `<img src="<?xml…">` is a broken image, so the
   * dialog showed its alt text and nothing else.
   */
  readonly qrCode: string;
}

export function TwoFactorDialog({
  stage,
  enrolment,
  recoveryCodes,
  busy = false,
  error,
  onVerify,
  onDisable,
  onRegenerate,
  onClose,
}: {
  readonly stage: TwoFactorStage;
  /** Present on `enrol`. Absent while the enrolment request is still in flight. */
  readonly enrolment?: TwoFactorEnrolment | null;
  /** Present on `keys`. */
  readonly recoveryCodes?: readonly string[];
  readonly busy?: boolean;
  /** Already translated. */
  readonly error?: string | null;
  readonly onVerify: (code: string) => void;
  readonly onDisable: (password: string) => void;
  readonly onRegenerate: (password: string) => void;
  readonly onClose: () => void;
}) {
  const t = useT();
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const title =
    stage === 'enrol'
      ? t('account.twofa.title')
      : stage === 'keys'
        ? t('account.twofa.keys.title')
        : stage === 'disable'
          ? t('account.twofa.disable')
          : t('account.twofa.keys.regenerate');

  /**
   * ⚠️ **The keys as one block of text, which is what both affordances want.** A copy that produced
   * a JSON array, or a download of one key per file, would be a worse version of the thing
   * somebody is about to paste into a password manager.
   */
  const keyBlock = (recoveryCodes ?? []).join('\n');

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        /* ⚠️ **The keys stage refuses to close from the scrim or the X**, because closing it
           throws away something unrecoverable. Its own button is the way out, and it says what
           leaving means. */
        if (!next && stage !== 'keys') onClose();
      }}
      title={title}
      width={440}
      closeLabel={t('common.close')}
    >
      {stage === 'enrol' && (
        <div className="flex flex-col gap-3">
          <p className="text-[12.5px]">{t('account.twofa.scan')}</p>

          {enrolment == null ? (
            <div className="bg-muted h-[184px] animate-pulse rounded-md" />
          ) : (
            <>
              {/* ⚠️ A white plate under the code, always, whatever the theme: a QR reader needs the
                  light modules lighter than the dark ones, and GoTrue's SVG draws only the dark
                  ones. On a dark surface it is unreadable rather than merely ugly. The plate pins
                  itself to the light palette, as the kit's `QrCode` does, so its `--background`
                  is the light theme's white in every theme. */}
              <div data-theme="light" className="bg-background flex justify-center rounded-md p-3">
                {/**
                 * ⚠️⚠️ **WRAPPED INTO A `data:` URL RATHER THAN INJECTED AS MARKUP.** GoTrue hands
                 * us an SVG document; `dangerouslySetInnerHTML` would render somebody else's
                 * markup inside our page, and an `<svg>` can carry a `<script>`. As an image source
                 * it is inert whatever arrives, which is the property worth keeping for a string
                 * that crosses a network boundary.
                 *
                 * ⚠️ **`encodeURIComponent`, not base64.** The payload is text, the URL stays
                 * readable in a devtools panel, and `#` inside an SVG fill would otherwise end the
                 * data early — which is the failure this encoding exists to prevent.
                 */}
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(enrolment.qrCode)}`}
                  alt={t('account.twofa.qrAlt')}
                  width={160}
                  height={160}
                />
              </div>

              <p className="text-muted-foreground text-[11.5px]">{t('account.twofa.secret')}</p>
              {/* Selectable and spaced out: this is typed into another device by hand when a
                  camera is not available. */}
              <code className="bg-muted rounded px-2 py-1 text-center text-[12px] tracking-[0.12em] break-all">
                {enrolment.secret}
              </code>

              <Field label={t('account.twofa.code')}>
                <TextInput
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  className="text-center tracking-[0.4em] tabular-nums"
                  value={code}
                  onChange={(event) => {
                    setCode(event.target.value.replace(/\D/gu, '').slice(0, 6));
                  }}
                />
              </Field>

              {/* ⚠️ Said out loud: enrolling is not enabling. Without this line somebody closes the
                  tab after scanning and believes two-factor is on. */}
              <p className="text-muted-foreground text-[11.5px]">{t('account.twofa.notOnYet')}</p>

              {error == null ? null : (
                <p role="alert" className="text-destructive text-[11.5px]">
                  {error}
                </p>
              )}

              <Button
                className="w-full"
                disabled={busy || !/^\d{6}$/u.test(code)}
                onClick={() => {
                  onVerify(code);
                }}
              >
                {busy ? t('account.saving') : t('account.twofa.verify')}
              </Button>
            </>
          )}
        </div>
      )}

      {stage === 'keys' && (
        <div className="flex flex-col gap-3">
          {/**
           * ⚠️⚠️ **THE WARNING COMES FIRST, ABOVE THE KEYS.** Underneath them it is read after the
           * decision it is trying to inform — by which point somebody has already glanced at a
           * list of codes and reached for the close button.
           */}
          <p className="border-destructive/40 bg-destructive/10 rounded-md border px-3 py-2 text-[12px]">
            {t('account.twofa.keys.once')}
          </p>

          <ul className="bg-muted grid grid-cols-2 gap-x-4 gap-y-1 rounded-md p-3 text-[12.5px] tabular-nums">
            {(recoveryCodes ?? []).map((key) => (
              <li key={key} className="text-center tracking-wide">
                {key}
              </li>
            ))}
          </ul>

          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 justify-center gap-2"
              onClick={() => {
                /* ⚠️ Read through a widened global and guarded on the CALL: `navigator.clipboard`
                   is typed as always present and is absent on an insecure origin and in jsdom —
                   `integrations-section.tsx` records the same shape. */
                const clipboard = (
                  navigator as unknown as {
                    clipboard?: { writeText?: (text: string) => Promise<void> };
                  }
                ).clipboard;
                void clipboard?.writeText?.(keyBlock).then(
                  () => {
                    setCopied(true);
                  },
                  () => {
                    /* A refused clipboard says nothing and leaves the download, which always
                       works. Telling somebody their copy failed while they can plainly see the
                       keys on screen would be noise. */
                  },
                );
              }}
            >
              <Icon name={copied ? 'check' : 'copy'} size={14} />
              {copied ? t('common.copied') : t('common.copy')}
            </Button>

            {/**
             * ⚠️ **A Blob and an object URL, not a `data:` link.** A `data:text/plain` href is
             * blocked as a top-level navigation by every current browser, so the download would
             * silently do nothing — and `revokeObjectURL` afterwards, or the page leaks the blob
             * for as long as it lives.
             */}
            <Button
              variant="outline"
              size="sm"
              className="flex-1 justify-center gap-2"
              onClick={() => {
                const url = URL.createObjectURL(new Blob([keyBlock], { type: 'text/plain' }));
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = 'forecastify-recovery-keys.txt';
                anchor.click();
                URL.revokeObjectURL(url);
              }}
            >
              <Icon name="download" size={14} />
              {t('account.twofa.keys.download')}
            </Button>
          </div>

          <Button
            className="w-full"
            onClick={() => {
              onClose();
            }}
          >
            {t('account.twofa.keys.saved')}
          </Button>
        </div>
      )}

      {(stage === 'disable' || stage === 'regenerate') && (
        <div className="flex flex-col gap-3">
          <p className="text-[12.5px]">
            {stage === 'disable'
              ? t('account.twofa.disableNote')
              : t('account.twofa.keys.regenerateNote')}
          </p>

          <Field label={t('account.password.current')}>
            <TextInput
              type="password"
              autoComplete="current-password"
              autoFocus
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </Field>

          {error == null ? null : (
            <p role="alert" className="text-destructive text-[11.5px]">
              {error}
            </p>
          )}

          <div className="flex gap-2">
            <Button
              variant="ghost"
              className="flex-1 justify-center"
              onClick={() => {
                onClose();
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant={stage === 'disable' ? 'destructive' : 'default'}
              className="flex-1 justify-center"
              disabled={busy || password === ''}
              onClick={() => {
                if (stage === 'disable') onDisable(password);
                else onRegenerate(password);
              }}
            >
              {busy
                ? t('account.saving')
                : stage === 'disable'
                  ? t('account.twofa.disable')
                  : t('account.twofa.keys.regenerate')}
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
